'use client';

import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import Pagination from '@/components/commons/pagination';
import TabProductGroup from '@/components/sections/builder/elements/body/cart-total/component/tab-product-group';
import TabProductItem from '@/components/sections/builder/elements/body/cart-total/component/tab-product-item';
import { Button } from '@/components/ui/button';
import { useGetRewardProduct, useGetRewardProductItem } from '@/hooks/api/storefront/promotion';
import { useDebounceValue } from '@/hooks/use-debounce';
import { cn, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { useCallback, useEffect, useState } from 'react';

type ModalGiftRewardProps = {
  openModal: boolean;
  promotion: BasePromotion;
  token: string;
  setOpenModal: (open: boolean) => void;
  setPromotion?: (promo: BasePromotion | null) => void;
  maxQty: number;
  pendingPromotion?: BasePromotion;
  onSaveGift: (data: BaseFreeGift[]) => void;
  selectedTab: 'product_group' | 'product_variant';
  setSelectedTab: React.Dispatch<React.SetStateAction<'product_group' | 'product_variant'>>;
  colorScheme?: ThemeColorSchemeType;
  setSelectedPromotion?: (promotion: BasePromotion[]) => void;
  initialSelectedProducts?: BaseFreeGift[];
};

export default function ModalGiftReward({
  openModal,
  promotion,
  token,
  setOpenModal,
  setPromotion,
  maxQty,
  onSaveGift,
  pendingPromotion,
  selectedTab,
  setSelectedTab,
  colorScheme,
  setSelectedPromotion,
  initialSelectedProducts = [],
}: ModalGiftRewardProps) {
  const [selectedProducts, setSelectedProducts] = useState<BaseFreeGift[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<BasePromotionRewardProduct>();

  const [paramsProductGroup, setParamsProductGroup] = useState({
    page: 1,
    pageSize: 10,
    q: '',
  });
  const [paramsProductItem, setParamsProductItem] = useState({
    page: 1,
    pageSize: 20,
    q: '',
  });

  const debouncedSearchProduct = useDebounceValue(
    selectedTab === 'product_group' ? paramsProductGroup.q : paramsProductItem.q,
    500
  );

  const {
    data: listRewardProduct,
    totalCount: totalCountGroup,
    isLoading: isLoadingGroup,
    isRefetching: isRefetchingGroup,
  } = useGetRewardProduct(
    {
      promotion_id: promotion?.promotion_id as string,
      promotion_tier_id: promotion?.promotion_tier_id as string,
      token,
    },
    {
      page: paramsProductGroup.page,
      pageSize: paramsProductGroup.pageSize,
      q: debouncedSearchProduct,
    },
    {
      enabled: openModal,
    }
  );

  const {
    data: listProductItem,
    totalCount: totalCountItem,
    isLoading: isLoadingItem,
  } = useGetRewardProductItem(
    {
      promotion_id: promotion?.promotion_id as string,
      product_id: selectedProduct?.product_group_id as string,
      promotion_tier_id: promotion?.promotion_tier_id as string,
      token,
    },
    {
      page: paramsProductItem.page,
      pageSize: paramsProductItem.pageSize,
      q: debouncedSearchProduct,
    },
    {
      enabled: selectedTab === 'product_variant' && !!selectedProduct,
    }
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedTab === 'product_group') {
        setParamsProductGroup((prev) => ({
          ...prev,
          q: e.target.value,
          page: 1,
        }));
      } else {
        setParamsProductItem((prev) => ({
          ...prev,
          q: e.target.value,
          page: 1,
        }));
      }
    },
    [selectedTab]
  );

  const totalQty = selectedProducts.reduce(
    (sum, group) => sum + group.items.reduce((gSum, item) => gSum + (item.qty ?? 0), 0),
    0
  );

  const handleUpdateQty = (
    productGroup: BasePromotionRewardProduct,
    item: BasePromotionRewardProductItem,
    delta: number
  ) => {
    setSelectedProducts((prev) => {
      const updated = prev.map((group) => {
        if (group.product_group.product_group_id !== productGroup.product_group_id) return group;

        const itemExists = group.items.find((i) => i.item_id === item.item_id);
        let updatedItems: typeof group.items;

        if (itemExists) {
          const newQty = (itemExists.qty ?? 0) + delta;
          if (newQty <= 0) {
            // Remove item if qty becomes 0 or less
            updatedItems = group.items.filter((i) => i.item_id !== item.item_id);
          } else {
            // Update qty
            updatedItems = group.items.map((i) =>
              i.item_id === item.item_id ? { ...i, qty: newQty } : i
            );
          }
        } else {
          // Add new item if delta > 0
          updatedItems =
            delta > 0
              ? [
                  ...group.items,
                  {
                    ...item,
                    qty: delta,
                  },
                ]
              : group.items;
        }

        return updatedItems.length > 0 ? { ...group, items: updatedItems } : null; // group kosong, hapus
      });

      const cleaned = updated.filter((g): g is BaseFreeGift => g !== null);

      // If group not found at all, add it if delta > 0
      const groupExists = prev.some(
        (g) => g.product_group.product_group_id === productGroup.product_group_id
      );
      if (!groupExists && delta > 0) {
        cleaned.push({
          product_group: productGroup,
          items: [
            {
              ...item,
              qty: delta,
            },
          ],
        });
      }

      return cleaned;
    });
  };

  useEffect(() => {
    if (!openModal) {
      setPromotion?.(null);
      setSelectedProducts([]);
      setParamsProductGroup({
        page: 1,
        pageSize: 10,
        q: '',
      });
      setParamsProductItem({
        page: 1,
        pageSize: 20,
        q: '',
      });
      setSelectedTab('product_group');
    } else {
      if (initialSelectedProducts) {
        setSelectedProducts(initialSelectedProducts);
      }
    }
  }, [openModal]);

  useEffect(() => {
    if (selectedTab === 'product_group') {
      setParamsProductItem({
        page: 1,
        pageSize: 20,
        q: '',
      });
    }
  }, [selectedTab]);

  return (
    <ModalResponsive
      openModal={openModal}
      title={`Choose Your Free Gift (${totalQty}/${maxQty})`}
      setOpenModal={setOpenModal}
      className={cn(
        'z-[999] max-w-4xl !rounded-none',
        selectedTab === 'product_variant' && '!gap-0'
      )}
    >
      {selectedTab === 'product_group' ? (
        <TabProductGroup
          colorScheme={colorScheme}
          params={paramsProductGroup}
          isLoading={isLoadingGroup}
          isRefetching={isRefetchingGroup}
          listRewardProduct={listRewardProduct || []}
          handleSearch={handleSearch}
          setSelectedProduct={setSelectedProduct}
          selectedProducts={selectedProducts}
          setSelectedTab={setSelectedTab}
        />
      ) : (
        <TabProductItem
          colorScheme={colorScheme}
          selectedProduct={selectedProduct as BasePromotionRewardProduct}
          handleUpdateQty={handleUpdateQty}
          selectedProducts={selectedProducts}
          maxQty={maxQty}
          totalQty={totalQty}
          params={paramsProductItem}
          handleSearch={handleSearch}
          isLoading={isLoadingItem}
          listProductItem={listProductItem || []}
        />
      )}
      <ModalResponsiveFooter className='flex items-center !justify-between text-sm'>
        <div className='flex items-center gap-2'>
          <Pagination
            currentPage={
              selectedTab === 'product_group' ? paramsProductGroup.page : paramsProductItem.page
            }
            totalCount={
              selectedTab === 'product_group'
                ? (totalCountGroup as number)
                : (totalCountItem as number)
            }
            pageSize={
              selectedTab === 'product_group'
                ? paramsProductGroup.pageSize
                : paramsProductItem.pageSize
            }
            onPageChange={(page) => {
              if (selectedTab === 'product_group') {
                setParamsProductGroup((prev) => ({
                  ...prev,
                  page,
                }));
              } else {
                setParamsProductItem((prev) => ({
                  ...prev,
                  page,
                }));
              }
            }}
            onPageSizeChange={(size) => {
              if (selectedTab === 'product_group') {
                setParamsProductGroup((prev) => ({
                  ...prev,
                  pageSize: size,
                  page: 1,
                }));
              } else {
                setParamsProductItem((prev) => ({
                  ...prev,
                  pageSize: size,
                  page: 1,
                }));
              }
            }}
            isShowPageSize={false}
            usePaginationRange={false}
            totalSiblingCount={3}
            colorScheme={colorScheme}
          />
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            onClick={() => {
              if (selectedTab === 'product_group') {
                if (
                  pendingPromotion &&
                  Array.isArray(pendingPromotion.discount_reward) &&
                  pendingPromotion.discount_reward.length > 0
                ) {
                  setSelectedPromotion?.([pendingPromotion]);
                }
                setOpenModal(false);
              } else {
                setSelectedTab('product_group');
              }
            }}
            className='font-normal'
          >
            {selectedTab === 'product_group' ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant={isEmpty(colorScheme) ? 'default' : null}
            className='font-normal'
            style={
              !isEmpty(colorScheme)
                ? {
                    color: colorScheme?.btnSolidlabel,
                    backgroundColor: colorScheme?.btnSolidBackgroud,
                  }
                : {}
            }
            onClick={() => {
              const selected: BaseFreeGift[] = [];

              selectedProducts.forEach((group) => {
                group.items.forEach((item) => {
                  if (item.qty && item.qty > 0) {
                    selected.push({
                      promotion_id: promotion.promotion_id,
                      product_group: group.product_group,
                      items: [{ ...item, qty: item.qty }],
                    });
                  }
                });
              });

              onSaveGift(selected);
              setOpenModal(false);
            }}
            disabled={totalQty === 0}
          >
            Choose
          </Button>
        </div>
      </ModalResponsiveFooter>
    </ModalResponsive>
  );
}
